import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  Trash2,
  Upload,
  ArrowDownToLine,
  ArrowUpFromLine,
  Pencil,
  Check,
  X,
  Plus,
  AlertCircle,
  Receipt,
} from "lucide-react";
import { bankTransactionsApi, bankAccountsApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import TransactionImportModal from "./TransactionImportModal";

interface BankAccount {
  _id: string;
  kind: string;
  name: string;
  institution: string | null;
  accountLast4: string | null;
  status: string;
}

interface BankTransaction {
  _id: string;
  bankAccountId: { _id: string; name: string; kind: string; accountLast4: string | null } | string;
  date: string;
  description: string;
  amountCents: number;
  runningBalanceCents: number | null;
  importBatchId: string;
  importedAt: string;
  importedBy: { name?: string; email?: string } | null;
  status: string;
}

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const statusBadge: Record<string, { bg: string; text: string }> = {
  imported: { bg: "bg-slate-100", text: "text-slate-700" },
  categorized: { bg: "bg-blue-100", text: "text-blue-700" },
  posted: { bg: "bg-emerald-100", text: "text-emerald-700" },
};

const LIMIT = 25;

const TransactionsTab: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Import modal
  const [showImport, setShowImport] = useState(false);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: "",
    type: "deposit" as "deposit" | "withdrawal",
    amount: "",
  });
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetCreateForm = () => {
    setCreateForm({
      date: new Date().toISOString().slice(0, 10),
      description: "",
      type: "deposit",
      amount: "",
    });
    setCreateError("");
  };

  const handleCreate = async () => {
    setCreateError("");
    if (!createForm.description.trim()) {
      setCreateError("Description is required.");
      return;
    }
    const amt = parseFloat(createForm.amount);
    if (isNaN(amt) || amt <= 0) {
      setCreateError("Amount must be greater than zero.");
      return;
    }
    const amountCents = Math.round(amt * 100) * (createForm.type === "withdrawal" ? -1 : 1);

    setCreateSubmitting(true);
    try {
      await bankTransactionsApi.create({
        bankAccountId: selectedAccountId,
        date: createForm.date,
        description: createForm.description.trim(),
        amountCents,
      });
      setShowCreate(false);
      resetCreateForm();
      loadTransactions();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to create transaction.";
      setCreateError(msg);
    } finally {
      setCreateSubmitting(false);
    }
  };

  // Inline editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    date: string;
    description: string;
    amountCents: number;
    runningBalanceCents: number | null;
  }>({ date: "", description: "", amountCents: 0, runningBalanceCents: null });
  const [saving, setSaving] = useState(false);

  const startEditing = (tx: BankTransaction) => {
    setEditingId(tx._id);
    setEditForm({
      date: new Date(tx.date).toISOString().slice(0, 10),
      description: tx.description,
      amountCents: tx.amountCents,
      runningBalanceCents: tx.runningBalanceCents,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await bankTransactionsApi.update(editingId, {
        date: editForm.date,
        description: editForm.description,
        amountCents: editForm.amountCents,
        runningBalanceCents: editForm.runningBalanceCents,
      });
      setEditingId(null);
      loadTransactions();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // Load bank accounts
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await bankAccountsApi.list({ status: "open" });
        const accts = res.data?.data || [];
        setAccounts(accts);
        if (accts.length > 0 && !selectedAccountId) {
          setSelectedAccountId(accts[0]._id);
        }
      } catch (err) {
        console.error("Failed to load bank accounts:", err);
      }
    };
    loadAccounts();
  }, []);

  const loadTransactions = useCallback(async () => {
    if (!selectedAccountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, any> = {
        bankAccountId: selectedAccountId,
        page,
        limit: LIMIT,
      };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await bankTransactionsApi.list(params);
      setTransactions(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to load transactions:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, page, dateFrom, dateTo]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const totalPages = Math.ceil(total / LIMIT);

  const handleDeleteBatch = async (batchId: string) => {
    if (!window.confirm("Delete all transactions from this import batch? Only unposted transactions will be removed.")) return;
    try {
      await bankTransactionsApi.deleteBatch(batchId);
      loadTransactions();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to delete batch");
    }
  };

  const selectedAccount = accounts.find((a) => a._id === selectedAccountId);
  const selectedAccountName = selectedAccount
    ? `${selectedAccount.name}${selectedAccount.accountLast4 ? ` (...${selectedAccount.accountLast4})` : ""}`
    : "";

  const accountOptions = accounts.map((a) => ({
    value: a._id,
    label: `${a.name}${a.accountLast4 ? ` (...${a.accountLast4})` : ""}  -  ${a.kind}`,
  }));

  // Group transactions by batch for undo button
  const batchIds = Array.from(new Set(transactions.map((t) => t.importBatchId)));

  return (
    <div>
      {/* Error Banner */}
      {errorMessage && (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="ml-auto text-red-400 hover:text-red-600 transition-colors">&times;</button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { resetCreateForm(); setShowCreate(true); }}
              disabled={!selectedAccountId}
              className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-bold rounded-xl bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Add Transaction
            </button>
            <button
              onClick={() => setShowImport(true)}
              disabled={!selectedAccountId}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              Import CSV
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Bank Account</label>
            <SearchableSelect
              options={accountOptions}
              value={selectedAccountId || null}
              onChange={(val) => { setSelectedAccountId(val || ""); setPage(1); }}
              placeholder="Select account"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">
          {total} transaction{total !== 1 ? "s" : ""}
        </p>
        {batchIds.length > 0 && (
          <div className="flex items-center gap-2">
            {batchIds.map((batchId) => (
              <button
                key={batchId}
                onClick={() => handleDeleteBatch(batchId)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-xl text-xs bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                title={`Undo import batch ${batchId}`}
              >
                <Trash2 className="h-3 w-3" />
                Undo Batch
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : !selectedAccountId ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">Select a bank account</p>
          <p className="text-sm text-slate-400 mt-1">Choose a bank account above to view its transactions</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">No transactions</p>
          <p className="text-sm text-slate-400 mt-1">
            Import a CSV file from your bank to see transactions here
          </p>
          <button
            onClick={() => setShowImport(true)}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm bg-brand hover:bg-brand-hover transition-colors"
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white text-left bg-brand">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold text-right">Amount</th>
                  <th className="px-4 py-3 font-semibold text-right">Balance</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.map((tx, i) => {
                  const badge = statusBadge[tx.status] || statusBadge.imported;
                  const isDeposit = tx.amountCents > 0;
                  const isEditing = editingId === tx._id;

                  if (isEditing) {
                    const editIsDeposit = editForm.amountCents > 0;
                    return (
                      <tr key={tx._id} className="bg-amber-50">
                        <td className="px-4 py-2">
                          <input
                            type="date"
                            value={editForm.date}
                            onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
                            className="w-full rounded-xl border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editForm.description}
                            onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                            className="w-full rounded-xl border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => setEditForm((f) => ({ ...f, amountCents: Math.abs(f.amountCents) || 0 }))}
                              className={`px-2 py-1 rounded-xl text-xs font-semibold transition-colors ${
                                editIsDeposit
                                  ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300"
                                  : "bg-slate-100 text-slate-500 hover:bg-emerald-50"
                              }`}
                            >
                              Deposit
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditForm((f) => ({ ...f, amountCents: -(Math.abs(f.amountCents) || 0) }))}
                              className={`px-2 py-1 rounded-xl text-xs font-semibold transition-colors ${
                                !editIsDeposit && editForm.amountCents !== 0
                                  ? "bg-red-100 text-red-700 ring-1 ring-red-300"
                                  : "bg-slate-100 text-slate-500 hover:bg-red-50"
                              }`}
                            >
                              Withdrawal
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={Math.abs(editForm.amountCents) || ""}
                            placeholder="0"
                            onChange={(e) => {
                              const abs = e.target.value ? Math.abs(parseInt(e.target.value, 10)) : 0;
                              const sign = editIsDeposit ? 1 : -1;
                              setEditForm((f) => ({ ...f, amountCents: abs * sign }));
                            }}
                            className="w-full rounded-xl border border-slate-300 px-2 py-1.5 text-sm text-right font-mono focus:outline-none focus:border-emerald-600"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="1"
                            value={editForm.runningBalanceCents ?? ""}
                            placeholder="-"
                            onChange={(e) => {
                              const val = e.target.value ? parseInt(e.target.value, 10) : null;
                              setEditForm((f) => ({ ...f, runningBalanceCents: val }));
                            }}
                            className="w-full rounded-xl border border-slate-300 px-2 py-1.5 text-sm text-right font-mono focus:outline-none focus:border-emerald-600"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
                          >
                            {tx.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={saveEdit}
                              disabled={saving}
                              className="p-1.5 rounded-xl text-white bg-brand hover:bg-brand-hover transition-colors"
                              title="Save"
                            >
                              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              onClick={cancelEditing}
                              disabled={saving}
                              className="p-1.5 rounded-xl text-slate-500 hover:bg-slate-200 transition-colors"
                              title="Cancel"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <motion.tr
                      key={tx._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.02 }}
                      className="hover:bg-slate-50"
                    >
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-4 py-3 text-slate-800 font-medium max-w-[350px] truncate">
                        {tx.description}
                      </td>
                      <td className="px-4 py-3">
                        {isDeposit ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                            <ArrowDownToLine className="h-3 w-3" />
                            Deposit
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold bg-red-50 text-red-600 ring-1 ring-red-200">
                            <ArrowUpFromLine className="h-3 w-3" />
                            Withdrawal
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        <span className={isDeposit ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>
                          {isDeposit ? "+" : "-"}{formatCents(Math.abs(tx.amountCents))}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-600">
                        {tx.runningBalanceCents !== null
                          ? formatCents(tx.runningBalanceCents)
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
                        >
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {tx.status === "imported" && (
                          <button
                            onClick={() => startEditing(tx)}
                            className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                            title="Edit transaction"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Transaction Modal */}
      <PremiumModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Add Transaction"
        subtitle="Create a manual bank transaction"
        icon={Receipt}
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              disabled={createSubmitting}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={createSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Add Transaction"
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          {createError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {createError}
            </div>
          )}

          {/* Type selector */}
          <div>
            <label className="block text-xs text-slate-500 mb-2">Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCreateForm((f) => ({ ...f, type: "deposit" }))}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                  createForm.type === "deposit"
                    ? "bg-emerald-50 text-emerald-700 ring-2 ring-emerald-400 shadow-sm"
                    : "bg-slate-50 text-slate-500 ring-1 ring-slate-200 hover:bg-emerald-50/50 hover:text-emerald-600"
                }`}
              >
                <ArrowDownToLine className="h-4 w-4" />
                Deposit
              </button>
              <button
                type="button"
                onClick={() => setCreateForm((f) => ({ ...f, type: "withdrawal" }))}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                  createForm.type === "withdrawal"
                    ? "bg-red-50 text-red-600 ring-2 ring-red-400 shadow-sm"
                    : "bg-slate-50 text-slate-500 ring-1 ring-slate-200 hover:bg-red-50/50 hover:text-red-500"
                }`}
              >
                <ArrowUpFromLine className="h-4 w-4" />
                Withdrawal
              </button>
            </div>
          </div>

          {/* Date + Amount row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Date</label>
              <input
                type="date"
                value={createForm.date}
                onChange={(e) => setCreateForm((f) => ({ ...f, date: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Amount ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={createForm.amount}
                onChange={(e) => setCreateForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-right font-mono focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Description</label>
            <input
              type="text"
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Office supplies, Client payment received"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Preview */}
          {createForm.amount && parseFloat(createForm.amount) > 0 && (
            <div className={`rounded-xl px-4 py-3 text-sm font-medium ${
              createForm.type === "deposit"
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                : "bg-red-50 text-red-600 ring-1 ring-red-200"
            }`}>
              {createForm.type === "deposit" ? "+" : "-"}
              {new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(parseFloat(createForm.amount))}
              {" "}
              {createForm.type === "deposit" ? "will be deposited" : "will be withdrawn"}
            </div>
          )}
        </div>
      </PremiumModal>

      {/* Import Modal */}
      <TransactionImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={loadTransactions}
        bankAccountId={selectedAccountId}
        bankAccountName={selectedAccountName}
      />
    </div>
  );
};

export default TransactionsTab;
